import { useState } from "react";
import AviationSectorPicker from "../../../components/Sectors/AviationSectorPicker";
import FormSubmitButton from "../../../components/FormSubmitButton";
import SubFlowHeader from "../../../components/GoalSubflow/SubFlowHeader";
import OptionPills from "../../../components/GoalSubflow/OptionPills";
import { useGoalSubflow } from "./useGoalSubflow";
import { getOptions } from "../../registry";
import { t } from "../../../../../locales/t";

/**
 * "Share your expertise" — the Be-a-Mentor sub-flow.
 *
 * The same two questions as `ReceiveMentorship`, asked the other way round, and
 * both differences are deliberate:
 *
 *   The picker runs in `offering` mode, so sectors with follow-ups ask what the
 *   member has experience in rather than what interests them.
 *
 *   The stage question is their OWN stage, so it is a single answer and it is
 *   required — a mentor with no stage cannot be matched against someone looking
 *   to learn from a particular one. `ReceiveMentorship` asks which stages they
 *   want to learn from, which is a preference, so it is multi and optional.
 *
 * Selecting this goal records the goal and these answers only. There is no
 * mentor application anywhere in onboarding — ECH-187 owns verification.
 */
const GOAL = "provide_mentorship";

const ProvideMentorship = () => {
  const { asks, index, total, answers, submit } = useGoalSubflow(GOAL);

  // Seeded from the flow so revisiting shows what was already answered.
  const [sectors, setSectors] = useState(
    () => answers.sectors ?? { primary: [], secondary: {} },
  );
  const [experienceStage, setExperienceStage] = useState(
    () => answers.experienceStage ?? "",
  );

  const canContinue =
    (!asks.sectors || sectors.primary.length > 0) &&
    (!asks.experienceStage || Boolean(experienceStage));

  const handleContinue = () => {
    if (!canContinue) return;
    submit({
      ...(asks.sectors ? { sectors } : {}),
      ...(asks.experienceStage ? { experienceStage } : {}),
    });
  };

  return (
    <SubFlowHeader
      goal={GOAL}
      index={index}
      total={total}
      onSubmit={handleContinue}
    >
      {asks.sectors && (
        <AviationSectorPicker
          value={sectors}
          onChange={setSectors}
          mode={asks.sectors}
          label={t(`onboarding.subflow.${GOAL}.sectorsLabel`)}
          subtitle={t(`onboarding.subflow.${GOAL}.sectorsSubtitle`)}
        />
      )}

      {asks.experienceStage && (
        <OptionPills
          options={getOptions("experienceStage")}
          value={experienceStage}
          onChange={setExperienceStage}
          label={t("onboarding.subflow.questions.experienceStage.label")}
        />
      )}

      <FormSubmitButton
        variant="contained"
        fullWidth
        disabledReason={!canContinue ? t("onboarding.subflow.incomplete") : ""}
        sx={{ py: 1.5 }}
      >
        {t("onboarding.subflow.continue")}
      </FormSubmitButton>
    </SubFlowHeader>
  );
};

export default ProvideMentorship;
